import { ActionButton } from "@components:ActionButton";
import { SuperInput } from "@components:SuperInput/SuperInput";
import { UploadZone } from "@components:UploadZone";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { LANGUAGE_CODES } from "@pages:app/audio/components/language";
import { RecorderCard } from "@pages:app/audio/components/RecorderCard";
import { RecordingCard } from "@pages:app/audio/components/RecordingCard";
import { RecordingPlayer } from "@pages:app/audio/components/RecordingPlayer";
import { SampleWaveform } from "@pages:app/audio/components/SampleWaveform";
import { LANGUAGE_AUTO_DETECT } from "@pages:app/audio/const";
import {
  AudioPlayer,
  TranscriptionJob,
  useMicrophoneRecorder,
} from "@pages:app/audio/hooks";
import { requestMeta } from "@pages:app/audio/util";
import { Trans, useTranslation } from "react-i18next";
import { MdCancel, MdMic } from "react-icons/md";

interface UploadFormProps {
  job: TranscriptionJob;
  player: AudioPlayer;
  canTranscribe: boolean;
}

/*
 * Pick a recording, or make one with the microphone. Once one is chosen it becomes
 * the recording card that stays on screen through processing and result, with its settings inside.
 */
export const UploadForm = ({ job, player, canTranscribe }: UploadFormProps) => {
  const { t } = useTranslation();
  const {
    file,
    waveform,
    language,
    setLanguage,
    numSpeakers,
    setNumSpeakers,
    isUploading,
    error,
    selectFile,
    clearFile,
    transcribe,
  } = job;
  const recorder = useMicrophoneRecorder();
  const recording = recorder.status !== "idle";
  const message = error || recorder.error;

  const settings = (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
      <Box sx={{ flex: 1 }}>
        <SuperInput.Autocomplete
          label={t("audio.upload.language")}
          disablePortal
          clearOnBlur={false}
          options={LANGUAGE_CODES}
          getOptionLabel={(option) => t(`audio.languages.${option}`)}
          renderInput={(params) => <TextField {...params} />}
          value={LANGUAGE_CODES.find((code) => code === language) ?? null}
          onChange={(_, newValue) => {
            setLanguage(newValue ?? LANGUAGE_AUTO_DETECT);
          }}
          noOptionsText={t("audio.upload.noLanguageOption")}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <SuperInput.NumberField
          label={t("audio.upload.numberOfSpeakers")}
          placeholder={t("audio.languages.autodetect")}
          min={1}
          max={10}
          step={1}
          required={false}
          value={numSpeakers ?? ""}
          onChange={(event) => {
            const raw = event.target.value;
            setNumSpeakers(
              raw ? Math.max(1, Math.min(parseInt(raw, 10), 10)) : null,
            );
          }}
        />
      </Box>
    </Stack>
  );

  const renderPicker = () => {
    if (file) {
      return (
        <RecordingCard
          file={file}
          meta={requestMeta(
            {
              file,
              durationSeconds: waveform?.durationSeconds,
              language: LANGUAGE_AUTO_DETECT,
              numSpeakers: null,
            },
            t,
          )}
          top={<RecordingPlayer player={player} waveform={waveform} reveal />}
          footer={settings}
        >
          <Button variant="outlined" onClick={clearFile} disabled={isUploading}>
            {t("audio.upload.changeFile")}
          </Button>
        </RecordingCard>
      );
    }
    if (recording) {
      return <RecorderCard recorder={recorder} onRecorded={selectFile} />;
    }
    return (
      <>
        <UploadZone
          size="default"
          onFileSelect={selectFile}
          accept="audio/*,video/mp4"
          title={t("audio.upload.dropzone.title")}
          subtitle={t("audio.upload.dropzone.subtitle")}
          hint={t("audio.upload.dropzone.hint")}
          fileIcon={<SampleWaveform />}
        />
        {recorder.supported && (
          <Button
            variant="outlined"
            startIcon={<MdMic />}
            onClick={recorder.start}
            sx={{ alignSelf: "center" }}
          >
            {t("audio.record.button")}
          </Button>
        )}
        <Typography
          sx={{
            alignSelf: "center",
            maxWidth: 520,
            fontSize: 14,
            color: "text.secondary",
            textAlign: "center",
          }}
        >
          <Trans
            i18nKey="audio.upload.detectionHint"
            components={{ br: <br /> }}
          />
        </Typography>
      </>
    );
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 720, mx: "auto" }}>
      {renderPicker()}

      {message && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          role="alert"
          sx={{ color: "error.main" }}
        >
          <MdCancel />
          <Typography variant="body2">{message}</Typography>
        </Stack>
      )}

      {file && (
        <ActionButton
          onClick={transcribe}
          disabled={!canTranscribe}
          loading={isUploading}
          loadingText={t("audio.upload.uploading")}
          fullWidth
        >
          {t("audio.upload.transcribe")}
        </ActionButton>
      )}
    </Stack>
  );
};
